import { motion } from "framer-motion";
import { ExternalLink, Camera, ScanLine, FileSpreadsheet } from "lucide-react";

const SHOWCASE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6ac18a15-ad29-4fd7-ae20-b9d3f0509294/images/7024c3cb6953dfe941a423f5a8d4c52dbab3d72b2134a5e52d1cb2174b66f973.jpeg";

const FEATURES = [
  { icon: Camera, text: "Snap or batch-upload photos" },
  { icon: ScanLine, text: "AI extracts names, text & categories" },
  { icon: FileSpreadsheet, text: "Live spreadsheet + CSV export" },
];

export default function FeaturedBuild() {
  return (
    <section id="work" className="relative py-24 sm:py-32 bg-[#0D1320]/60 overflow-hidden">
      <div
        className="absolute top-0 right-0 w-[460px] h-[460px] rounded-full bg-[#00F0FF]/[0.06] blur-[130px] pointer-events-none"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
            04 — Featured Build · Custom Solutions
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
            Custom solutions to inventory control, in production.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
            Not just theory — a working product built around real inventory
            workflows. SmartScan Inventory shows how custom tooling can sit on
            top of everyday operations and turn them into clean, structured data.
          </p>
        </motion.div>

        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-testid="featured-scantrack-card"
          className="sheen mt-14 grid lg:grid-cols-12 gap-0 rounded-3xl border border-white/[0.08] bg-[#111827]/80 overflow-hidden shadow-[0_0_90px_rgba(0,240,255,0.06)]"
        >
          <div className="lg:col-span-7 p-8 sm:p-11 flex flex-col justify-center">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00F0FF] border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] rounded-full px-3.5 py-1.5">
                Live product
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">
                scantrackexport.com
              </span>
            </div>
            <h3 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white">
              SmartScan Inventory
            </h3>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF]/90">
              Scan it. Name it. Track it.
            </p>
            <p className="mt-5 text-[15px] sm:text-base text-[#9CA3AF] leading-relaxed max-w-lg">
              AI-powered inventory for warehouses and field teams — point your
              camera, let the AI do the cataloguing, export a clean sheet in
              seconds. The same custom-thinking I bring to MRP migrations:
              practical tools that fit the way a team actually works.
            </p>
            <ul className="mt-7 space-y-3.5">
              {FEATURES.map((f) => (
                <li key={f.text} className="flex items-center gap-3.5 text-sm sm:text-[15px] text-[#D1D5DB]">
                  <span className="h-8 w-8 shrink-0 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                    <f.icon size={15} />
                  </span>
                  {f.text}
                </li>
              ))}
            </ul>
            <a
              href="https://scantrackexport.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="featured-scantrack-link"
              className="mt-9 self-start inline-flex items-center gap-2.5 font-display font-semibold text-[#0B0F17] bg-[#00F0FF] hover:bg-[#4DF7FF] rounded-full px-7 py-3.5 shadow-[0_10px_40px_rgba(0,240,255,0.2)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Explore SmartScan Inventory
              <ExternalLink size={16} />
            </a>
          </div>
          <div className="relative lg:col-span-5 min-h-[320px] lg:min-h-[520px]">
            <img
              src={SHOWCASE_IMG}
              alt="SmartScan Inventory app scanning a warehouse shelf with AI-generated item labels"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#111827] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-[#00F0FF]/[0.04] mix-blend-overlay" />
          </div>
        </motion.article>
      </div>
    </section>
  );
}
